import { Link } from "react-router-dom";
import { useQuery, getMyPlatformBuildings, getGlobalConfig } from "wasp/client/operations";
import {
  Building2,
  ExternalLink,
  MapPin,
  Shield,
  UserRound,
  ArrowLeft,
} from "lucide-react";
import { DashboardLayout } from "../../client/components/Sidebar";

type BuildingRow = {
  role: string;
  locationHint?: string | null;
  instance?: {
    id: string;
    slug: string;
    name: string;
    appUrl?: string | null;
    customDomain?: string | null;
    status: string;
    vertical?: string;
    brandName?: string;
  };
};

function roleLabel(role: string) {
  const r = (role || "").toUpperCase();
  if (r === "ADMIN") return "Admin";
  if (r === "MANAGER") return "Manager";
  return "Resident";
}

function RoleIcon({ role }: { role: string }) {
  const r = (role || "").toUpperCase();
  if (r === "ADMIN" || r === "MANAGER") {
    return <Shield className="h-3.5 w-3.5" />;
  }
  return <UserRound className="h-3.5 w-3.5" />;
}

function portalUrl(b: BuildingRow) {
  return b.instance?.customDomain || b.instance?.appUrl || "";
}

export default function MyBuildingsPage() {
  const { data, isLoading, error } = useQuery(getMyPlatformBuildings);
  const { data: globalConfig } = useQuery(getGlobalConfig);
  const payload = data as any;
  const brandName = (globalConfig as any)?.brandName || "Gedoba";

  const currentSlug = payload?.currentInstanceSlug || "";
  const all: BuildingRow[] = payload?.allBuildings || [];
  const others: BuildingRow[] = payload?.buildings || [];
  const current =
    all.find((b) => b.instance?.slug === currentSlug) ||
    (currentSlug
      ? ({
          role: "RESIDENT",
          instance: {
            id: "current",
            slug: currentSlug,
            name: brandName,
            brandName,
            status: "ACTIVE",
            appUrl: typeof window !== "undefined" ? window.location.origin : "",
          },
        } as BuildingRow)
      : null);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
        <Link
          to="/dashboard"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to portal
        </Link>

        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-2xl bg-sky-100 p-2.5 text-sky-800">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900">
              My buildings
            </h1>
            <p className="mt-2 max-w-xl text-neutral-600">
              Your portfolio across this platform. Open another portal you
              already belong to — same email, separate logins.
            </p>
            {payload?.email && (
              <p className="mt-1 text-sm text-neutral-500">
                Identity: <span className="font-medium">{payload.email}</span>
              </p>
            )}
          </div>
        </div>

        {isLoading && (
          <p className="mt-8 text-sm text-neutral-500">Loading buildings…</p>
        )}

        {error && (
          <p className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as any)?.message || "Could not load buildings"}
          </p>
        )}

        {!isLoading && !error && !payload?.configured && (
          <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
            <p className="font-semibold">Central identity not configured</p>
            <p className="mt-1 text-amber-900/80">
              This portal is not linked to the control plane yet. Ask the
              platform operator to set{" "}
              <code className="rounded bg-amber-100 px-1">CONTROL_PLANE_*</code>{" "}
              env vars.
            </p>
          </div>
        )}

        {!isLoading && payload?.configured && (
          <div className="mt-8 space-y-8">
            {current && (
              <section>
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
                  You are here
                </h2>
                <BuildingCard
                  building={current}
                  isCurrent
                  localHref="/dashboard"
                />
              </section>
            )}

            <section>
              <div className="mb-3 flex items-end justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-neutral-500">
                  Other buildings
                </h2>
                <span className="text-xs text-neutral-400">
                  {others.length} linked
                </span>
              </div>
              {others.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-neutral-200 bg-neutral-50 px-5 py-8 text-center text-sm text-neutral-600">
                  No other buildings yet. Join another portal with the same
                  email, or ask an operator to link you from the control plane.
                </div>
              ) : (
                <ul className="space-y-3">
                  {others.map((b) => (
                    <li key={b.instance?.id || b.instance?.slug}>
                      <BuildingCard building={b} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function BuildingCard({
  building,
  isCurrent,
  localHref,
}: {
  building: BuildingRow;
  isCurrent?: boolean;
  localHref?: string;
}) {
  const url = portalUrl(building);
  const title =
    building.instance?.brandName || building.instance?.name || "Building";
  const slug = building.instance?.slug || "";
  const vertical = building.instance?.vertical;
  const status = building.instance?.status;

  return (
    <article
      className={`rounded-3xl border p-4 sm:p-5 ${
        isCurrent
          ? "border-sky-300 bg-sky-50/70 ring-1 ring-sky-200"
          : "border-neutral-200 bg-white"
      }`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-lg font-semibold text-neutral-900">
              {title}
            </h3>
            {isCurrent && (
              <span className="rounded-full bg-sky-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Current
              </span>
            )}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-500">
            <span className="font-mono text-xs">{slug}</span>
            {vertical && (
              <>
                <span aria-hidden>·</span>
                <span className="capitalize">{vertical}</span>
              </>
            )}
            {status && status !== "ACTIVE" && (
              <>
                <span aria-hidden>·</span>
                <span className="text-amber-700">{status}</span>
              </>
            )}
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
            <RoleIcon role={building.role} />
            {roleLabel(building.role)}
            {building.locationHint && (
              <span className="inline-flex items-center gap-1 font-normal text-neutral-500">
                <MapPin className="h-3 w-3" />
                {building.locationHint}
              </span>
            )}
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {isCurrent && localHref ? (
            <Link
              to={localHref}
              className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-sky-700 px-4 text-sm font-semibold text-white"
            >
              Stay here
            </Link>
          ) : url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-2xl bg-neutral-900 px-4 text-sm font-semibold text-white"
            >
              Open portal
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <span className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-dashed border-neutral-300 px-4 text-sm text-neutral-400">
              URL pending
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
